import React, { useState, useRef, useEffect } from "react";
import { ArrowUp, Paperclip, Globe, X, Mic, AudioLines } from "lucide-react";
// import VoiceRecorder from "./VoiceRecorder";
import VoiceMode from "./VoiceMode";
import AITokenLimitTracker from "./AITokenLimitTracker";
import { uploadFileAPI, storeMessageToDB, checkUploadFile, checkInputTokens } from "../../../api";
import toast from "react-hot-toast";

const ChatInput = ({ onSend, loading, isDarkTheme, lastBotResponse, chat_id, addFileMessage, checkRemainingAITokens, isWebSearch, setIsWebSearch }) => {
  const [input, setInput] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [attachmentName, setAttachmentName] = useState(null);
  const [maxTokens, setMaxTokens] = useState(200000);
  const [currentTokens, setCurrentTokens] = useState(200000);
  const [isVoiceOpen, setIsVoiceOpen] = useState(false)
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachedFile, setAttachedFile] = useState(null);
  // AI response token limit tracking
  const [aiTokensUsed, setAiTokensUsed] = useState(0);
  const [aiTokenLimit, setAiTokenLimit] = useState(5000);
  const [isAiTokenLimitReached, setIsAiTokenLimitReached] = useState(false);
  const [tokenResetTime, setTokenResetTime] = useState(null);

  // State for PDF upload count with daily reset using localStorage
  const getInitialPdfCount = () => {
    const today = new Date().toLocaleDateString();
    const stored = localStorage.getItem("pdfUploadCount");
    const storedDate = localStorage.getItem("pdfUploadDate");
    if (stored && storedDate === today) {
      return parseInt(stored, 10);
    }
    // Reset if no count for today or date has changed
    localStorage.setItem("pdfUploadCount", "0");
    localStorage.setItem("pdfUploadDate", today);
    return 0;
  };
  const [pdfUploadCount, setPdfUploadCount] = useState(getInitialPdfCount);

  // Update localStorage whenever pdfUploadCount changes
  useEffect(() => {
    localStorage.setItem("pdfUploadCount", pdfUploadCount.toString());
  }, [pdfUploadCount]);

  // Set up token reset time when limit is first reached
  useEffect(() => {
    
    if (checkRemainingAITokens===false) {
      const now = new Date();
      setTokenResetTime({
        hours: now.getHours(),
        minutes: now.getMinutes()
      });
      setIsAiTokenLimitReached(true);

      // Schedule a reset for the next day
      const resetTime = new Date();
      resetTime.setDate(resetTime.getDate() + 1);
      resetTime.setHours(now.getHours(), now.getMinutes(), 0, 0);

      const timeUntilReset = resetTime - now;
      const resetTimer = setTimeout(() => {
        setAiTokensUsed(0);
        setIsAiTokenLimitReached(false);
      }, timeUntilReset);

      return () => clearTimeout(resetTimer);
    }
    else{
      setIsAiTokenLimitReached(false);
    }
  }, [aiTokensUsed, aiTokenLimit, isAiTokenLimitReached, checkRemainingAITokens]);

  // Track AI response tokens
  useEffect(() => {
    // Only run if lastBotResponse changes and isn't empty
    if (lastBotResponse && lastBotResponse !== "") {
      // Estimate tokens in the AI response (rough estimate: ~4 chars per token)
      const estimatedTokens = Math.ceil(lastBotResponse.length / 4);
      setAiTokensUsed(prevTokens => {
        const newTotal = prevTokens + estimatedTokens;
        if (newTotal >= aiTokenLimit && !isAiTokenLimitReached) {
          setIsAiTokenLimitReached(true);

          // Set the reset time when limit is first reached
          const now = new Date();
          setTokenResetTime({
            hours: now.getHours(),
            minutes: now.getMinutes()
          });
        }
        return newTotal;
      });
    }
  }, [lastBotResponse, aiTokenLimit]);

  // Detect scrollbar appearance
  useEffect(() => {
    const textarea = textareaRef.current;
    const handleResize = () => {
      if (textarea) {
        setIsScrolling(textarea.scrollHeight > textarea.clientHeight);
      }
    };

    // Check when component mounts
    handleResize();

    // Resize observer to track changes
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update remaining tokens as user types (1 character = 1 token)
  useEffect(() => {
    setCurrentTokens(maxTokens - input.length);
  }, [input, maxTokens]);

  const handleSend = async () => {
    // If a file is attached, check the daily PDF limit immediately.
    const canUploadFile = await checkUploadFile();
    if (attachedFile && canUploadFile.can_upload===false) {
      toast.error("PDF upload limit reached");
      return;
    }
    if ((input.trim() || attachedFile) && !loading ) {
      const textTokens = await checkInputTokens(input);
      // If a file is attached, first upload and store its extracted content as a separate message.
      if (attachedFile) {
        try {
          const formData = new FormData();
          formData.append("file", attachedFile);
          const fileResponse = await uploadFileAPI(formData);

          if (fileResponse.uploadPercentage < 100) {
            toast.error(
              `Only ${fileResponse.uploadPercentage}% of your PDF has been uploaded due to larger size`
            );
          }

          const fileMsg = `${attachmentName}\nExtracted Content:\n${fileResponse.extracted_text}`;
          console.log(fileMsg);
          await storeMessageToDB("file", chat_id, fileMsg);
          // Add file message to UI immediately 
          addFileMessage({ sender: 'file', message: fileMsg });

          setPdfUploadCount((prev) => prev + 1);
        } catch (error) {
          console.error("File upload error:", error);
          return;
        }
      }
      else {
        // For text-only messages, validate token count.
        if (textTokens.valid===false) {
          toast.error("Token limit exceeded");
          return;
        }
      }

      // Send user query after file msg stored
      if (input.trim()) {
        onSend(input);
      }
      setInput("");
      setAttachmentName(null);
      setAttachedFile(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      setCurrentTokens((maxTokens - textTokens ));
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Enforce token limit (each character is one token)
    if (newValue.length <= maxTokens) {
      setInput(newValue);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
      }
    } else {
      // Optionally, trim to max allowed tokens
      setInput(newValue.slice(0, maxTokens));
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentName(file.name);
      setAttachedFile(file)
    }
  };

  const removeAttachment = () => {
    setAttachmentName(null);
    setAttachedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleWebSearchClick = () => {
    setIsWebSearch((prevState) => !prevState);
  };

  return (
    <div className="flex flex-col items-center w-[94%] md:w-[60%] mx-auto">
      {/* AI Token Limit Notification */}
      <AITokenLimitTracker
        isDarkTheme={isDarkTheme}
        isVisible={isAiTokenLimitReached}
        resetTime={tokenResetTime}
      />
      {attachmentName && (
        <div className="w-full bg-amber-50 mb-2 py-2 px-4 rounded-lg flex justify-between items-center text-sm">
          <div className="flex items-center">
            <Paperclip className="w-4 h-4 text-amber-500 mr-2" />
            <span className="text-gray-700 truncate max-w-xs">
              {attachmentName}
            </span>
          </div>
          <button
            onClick={removeAttachment}
            className="text-gray-500 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div
        className={`flex flex-col border sticky bottom-[14px] mx-auto box-border w-full shadow-md transition-all duration-300 ease-in-out
                ${isScrolling ? "rounded-lg" : "rounded-xl"} 
                ${isFocused ? "ring-2 ring-amber-300/70" : ""}
                ${isDarkTheme
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
          }`}
      >
        {/* First row: Textarea and Send button */}
        <div className="flex items-center p-2 w-full">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                // First prevent default for all Enter presses without shift
                e.preventDefault();

                // Only process the send if not loading
                if (!loading && !isAiTokenLimitReached) {
                  handleSend();
                }
              }
            }}
            placeholder={isAiTokenLimitReached ? "AI paused until tomorrow" : "Ask Petroleum BOT"}
            rows={1}
            className={`input flex-1 bg-transparent p-2 text-sm outline-none resize-none overflow-y-auto max-h-[150px] transition-all duration-300 ease-in-out
              ${isDarkTheme ? "text-gray-300" : "text-gray-800"}`}
          />
          <button
            onClick={handleSend}
            className={`${isAiTokenLimitReached ? 'bg-gray-400 hover:bg-gray-500' : 'bg-amber-500 hover:bg-amber-600'} text-white rounded-full p-1.5 transition-colors ml-2`}
            disabled={loading || !input.trim() || isAiTokenLimitReached}
            title="Send message"
          >
            <ArrowUp className={`w-5 h-5 ${!input.trim() || isAiTokenLimitReached ? 'opacity-70' : ''}`} />
          </button>
        </div>

        {/* Second row: Attachment, Voice Recorder (replacing mic button), Web search, and Tokens */}
        <div className={`flex items-center justify-between px-3 py-2 border-t ${isDarkTheme ? "border-gray-700" : "border-gray-200"
          }`}>
          <div className="flex items-center space-x-2 text-gray-500">
            <button
              onClick={handleFileClick}
              className="hover:text-amber-500 transition-colors p-1 rounded-full hover:bg-amber-50"
              title="Attach file"
              disabled={isAiTokenLimitReached}
            >
              <Paperclip className="w-5 h-5" />
            </button>

            {/* Replace the default mic button with the inline VoiceRecorder */}
            {/* <VoiceRecorder
              inline
              onVoiceInput={(transcript) => setInput(transcript)}
              recorderButtonRef={null}
            /> */}

            <button
              className={`p-1 rounded-full transition-colors ${isWebSearch ? "text-amber-500 bg-amber-50" : "text-gray-500 hover:bg-amber-50"
                }`}
              title="Web search"
              onClick={handleWebSearchClick}
              disabled={isAiTokenLimitReached}
            >
              <Globe className="w-5 h-5" />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={isAiTokenLimitReached}
            />

            <button
              onClick={() => setIsVoiceOpen(true)}
              className="hover:text-amber-500 transition-colors p-1 rounded-full hover:bg-amber-50"
              title="Voice mode"
              disabled={loading || isAiTokenLimitReached}
            >
              <AudioLines className="w-5 h-5" />
            </button>
          </div>

        </div>
      </div>

      <div className="text-xs text-gray-500 mt-2 mb-2 text-center">
        This Chatbot can make mistakes. Verify important details.
      </div>

      {isVoiceOpen && (
        <VoiceMode
          isOpen={isVoiceOpen}
          onClose={() => setIsVoiceOpen(false)}
          onSend={(message) => {
            // Handle both user and assistant messages
            onSend(message.message);
          }}
          isDarkTheme={isDarkTheme}
          botResponse={lastBotResponse}
          disabled={isAiTokenLimitReached}
        />
      )}
    </div>
  );

};

export default ChatInput;
